import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { dispatch } from '@ngneat/effects';
import { environmentProvider } from '@shared/environment-provider';
import { getCurrentAuthenticatedUser } from '@tmx/auth';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import { SalesforceProperties } from '../../models/salesforce-properties';
import { setOnboardingShifted } from '../../repositories/ams/ams.actions';
import { isFeatureEnabled } from '../../repositories/feature-flags/feature-flags.repository';
import { BrazeEventsService } from '../../services/braze-events.service';
import { SalesforceChatService } from '../../services/salesforce-chat.service';
import { FcWidget } from './models/fc-widget';

@Component({
  selector: 'lib-support-chat',
  templateUrl: './support-chat.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TooltipModule, TranslocoModule],
})
export class SupportChatComponent implements OnDestroy {
  private readonly brazeEventsService = inject(BrazeEventsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly salesforceChatService = inject(SalesforceChatService);
  private fcWidget: FcWidget | undefined = undefined;

  isFacade = signal(true);
  disabled = signal(true);
  private shouldAutoOpenChat = false;
  private isChatActive = false;
  private salesforceInitialized = false;
  private freshchatInitialized = false;
  private facadeRemoved = false;
  tooltipText = this.translocoService.translate(
    'components.supportChat.availableToolTip'
  );

  get isSalesforceEnabled() {
    return isFeatureEnabled('salesforce');
  }

  private isSalesforceEmbeddedService(
    obj: unknown
  ): obj is SalesforceProperties {
    return (
      typeof obj === 'object' &&
      obj !== null &&
      'init' in obj &&
      'settings' in obj
    );
  }

  private getSalesforceService() {
    if (Reflect.has(window, 'embeddedservice_bootstrap')) {
      const service: unknown = Reflect.get(window, 'embeddedservice_bootstrap');
      return this.isSalesforceEmbeddedService(service) ? service : null;
    }
    return null;
  }

  private waitForSalesforceReady(): Promise<void> {
    return new Promise((resolve) => {
      if (this.getSalesforceService()) {
        resolve();
        return;
      }

      const onReady = () => {
        window.removeEventListener('onEmbeddedMessagingReady', onReady);
        resolve();
      };

      window.addEventListener('onEmbeddedMessagingReady', onReady);
    });
  }

  initSalesforce = async (): Promise<void> => {
    this.salesforceInitialized = true;

    this.tooltipText = this.translocoService.translate(
      'components.supportChat.connectingToolTip'
    );

    if (this.shouldAutoOpenChat) {
      this.salesforceChatService.setShouldAutoLaunch(true);
    }

    this.salesforceChatService.loadAndInitialize();

    await this.waitForSalesforceReady();

    this.tooltipText = this.translocoService.translate(
      'components.supportChat.availableToolTip'
    );
    this.disabled.set(false);

    if (this.isFacade()) {
      this.facadeRemoved = true;
      this.isFacade.set(false);
    }

    this.setupSalesforceEventListeners();
  };

  initFreshchat = () => {
    this.freshchatInitialized = true;

    const script = document.createElement('script');
    script.setAttribute(
      'src',
      environmentProvider.getEnvironment().accountManagerSite.freshchat
        .widgetUrl
    );

    const makeUnavailable = () => {
      this.tooltipText = this.translocoService.translate(
        'components.supportChat.unavailableToolTip'
      );
      this.isFacade.set(false);
    };

    script.onerror = makeUnavailable;

    script.onload = () => {
      this.fcWidget = (window as unknown as { fcWidget?: FcWidget }).fcWidget;
      if (!this.fcWidget) {
        makeUnavailable();
        return;
      }

      this.fcWidget.init({
        ...environmentProvider.getEnvironment().accountManagerSite.freshchat
          .settings,
        externalId: getCurrentAuthenticatedUser().userId,
      });

      this.fcWidget.on('widget:loaded', () => {
        this.tooltipText = this.translocoService.translate(
          'components.supportChat.availableToolTip'
        );
        this.disabled.set(false);

        if (this.isFacade()) {
          this.facadeRemoved = true;
          this.isFacade.set(false);
          return;
        }

        this.fcWidget?.open();
      });
    };

    document.body.appendChild(script);
  };

  removeFacade = () => {
    this.facadeRemoved = true;
    this.shouldAutoOpenChat = true;
    this.isFacade.set(false);

    this.tooltipText = this.translocoService.translate(
      'components.supportChat.connectingToolTip'
    );

    if (this.isSalesforceEnabled) {
      this.salesforceChatService.setShouldAutoLaunch(true);
      if (!this.salesforceInitialized) {
        this.initSalesforce();
      }
    } else if (!this.freshchatInitialized) {
      this.initFreshchat();
    }
  };

  toggleShowChat() {
    if (this.isSalesforceEnabled) {
      this.openSalesforceChat();
    } else {
      this.openFreshchat();
    }
  }

  private openFreshchat() {
    if (!this.fcWidget) {
      return;
    }

    if (this.fcWidget.isOpen()) {
      this.fcWidget.close();
      return;
    }

    this.brazeEventsService.logBrazeCustomEvents(
      this.brazeEventsService.OPENED_FRESHCHAT
    );

    this.fcWidget.user
      .setProperties({
        firstName: getCurrentAuthenticatedUser().firstName,
        lastName: getCurrentAuthenticatedUser().lastName,
        email: getCurrentAuthenticatedUser().username,
        account: getCurrentAuthenticatedUser().accountName,
      })
      .then(() => {
        this.fcWidget?.open();
      });
  }

  private openSalesforceChat() {
    const salesforceService = this.getSalesforceService();
    if (!salesforceService?.utilAPI?.launchChat) {
      this.shouldAutoOpenChat = true;
      this.salesforceChatService.setShouldAutoLaunch(true);
      return;
    }

    this.shouldAutoOpenChat = false;
    this.salesforceChatService.setShouldAutoLaunch(false);

    this.salesforceChatService.launchChat();
  }

  private setupSalesforceEventListeners() {
    window.addEventListener('onEmbeddedMessagingWindowMinimized', () => {
      this.isChatActive = true;
      this.updateChatState();
    });

    window.addEventListener('onEmbeddedMessagingWindowMaximized', () => {
      this.isChatActive = true;
      this.updateChatState();
    });

    window.addEventListener('onEmbeddedMessagingWindowClosed', () => {
      this.isChatActive = false;
      this.updateChatState();
    });

    window.addEventListener('onEmbeddedMessagingConversationStart', () => {
      this.isChatActive = true;
      this.updateChatState();
    });

    window.addEventListener('onEmbeddedMessagingConversationEnd', () => {
      this.isChatActive = false;
      this.updateChatState();
    });
  }

  private updateChatState() {
    dispatch(setOnboardingShifted({ shifted: this.isChatActive }));
  }

  ngOnDestroy() {
    this.fcWidget?.destroy();
  }
}
